/**
 * Guide Page widget — in-app help articles.
 *
 * Mounts in the shell at `/guide` (public, module='shell') and `/admin/guide`
 * (admin management, no filter), and in module routes such as `/sales/guide`
 * (read+edit, module='sales').
 *
 * Storage: each article is one `vba_adminconfig` row with category=`guide`
 * and key=`guide.<slug>`. The row's `vba_value` JSON matches the
 * `GuideArticleValue` shape in @shared/entities/admin-config. The `modules`
 * array on each article controls which Guide pages it appears on.
 *
 * Initial content is seeded into Dataverse via
 * `npm run schema:seed-guide-articles` and `schema:seed-sales-guide-articles`.
 */

import { Fragment, useMemo, useRef, useState, useCallback } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Card,
  Checkbox,
  Dropdown,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Subtitle2,
  Textarea,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  AddRegular,
  BookRegular,
  DeleteRegular,
  DismissRegular,
  EditRegular,
  EyeRegular,
  ReOrderDotsVerticalRegular,
  SaveRegular,
} from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { veracityColors } from '../../../ui';
import { useAuth } from '../../../providers/AuthProvider';
import { hasGlobalAdmin } from '../../../auth';
import {
  useAdminConfigs,
  useUpdateConfig,
  useCreateConfig,
  useDeleteConfig,
  GUIDE_MODULES,
  type AdminConfig,
  type GuideArticleValue,
  type GuideModule,
} from '../../../entities/admin-config';

// =============================================================================
// Public API
// =============================================================================

export interface GuidePageProps {
  /**
   * If set, only articles whose `modules` array includes this value are
   * shown, and "New article" creates rows tagged with this module.
   * If undefined, all articles are shown (admin management mode) and new
   * articles default to `['shell']`.
   */
  moduleFilter?: GuideModule;
  /**
   * If true, admins ignore `moduleFilter` and see articles from every
   * module — useful on the shell `/guide` route so an admin gets a
   * one-stop view without needing to navigate to `/admin/guide`.
   * Non-admins are still constrained by `moduleFilter`.
   */
  adminSeesAll?: boolean;
  /**
   * If true, admins see the management controls (Edit, Delete, New article,
   * drag-to-reorder). Defaults to false so module routes like `/sales/guide`
   * are read-only — all editing happens at the Modules Home `/guide` route
   * and the `/admin/guide` admin tab.
   */
  allowAdminEdit?: boolean;
}

// =============================================================================
// Internal types
// =============================================================================

interface Article {
  /** Identifier — same as `value.slug`. */
  slug: string;
  /** Article payload. */
  value: GuideArticleValue;
  /** Dataverse row id. */
  configId: string;
  /** Dataverse rowVersion — used for optimistic concurrency. */
  rowVersion?: string;
}

// =============================================================================
// Styles
// =============================================================================

const useStyles = makeStyles({
  page: {
    display: 'grid',
    gridTemplateColumns: '260px 1fr',
    gap: tokens.spacingHorizontalL,
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXL}`,
    width: '100%',
    minHeight: 'calc(100vh - 64px)',
    boxSizing: 'border-box',
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    color: veracityColors.midnightCharcoal,
    fontWeight: tokens.fontWeightSemibold,
  },
  sidebarGroupHeader: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: veracityColors.duskBlue,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM} ${tokens.spacingVerticalXS}`,
  },
  sidebarItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    color: veracityColors.midnightCharcoal,
    fontSize: tokens.fontSizeBase300,
    ':hover': {
      backgroundColor: veracityColors.firstLight,
    },
  },
  sidebarItemActive: {
    backgroundColor: veracityColors.firstLight,
    fontWeight: tokens.fontWeightSemibold,
    borderLeft: `3px solid ${veracityColors.veracityOrange}`,
  },
  sidebarItemDragOver: {
    boxShadow: `inset 0 2px 0 0 ${veracityColors.veracityOrange}`,
  },
  sidebarItemDragging: {
    opacity: 0.4,
  },
  dragHandle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    color: veracityColors.duskBlue,
    cursor: 'grab',
    flexShrink: 0,
    ':active': { cursor: 'grabbing' },
  },
  sidebarFooter: {
    marginTop: tokens.spacingVerticalM,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${veracityColors.firstLight}`,
  },
  badgeRow: {
    display: 'flex',
    gap: '4px',
  },
  visibilityBadge: {
    fontSize: tokens.fontSizeBase100,
    color: veracityColors.duskBlue,
    backgroundColor: veracityColors.firstLight,
    padding: `2px 6px`,
    borderRadius: tokens.borderRadiusSmall,
  },
  moduleBadge: {
    fontSize: tokens.fontSizeBase100,
    color: '#fff',
    backgroundColor: veracityColors.duskBlue,
    padding: `2px 6px`,
    borderRadius: tokens.borderRadiusSmall,
  },
  content: {
    backgroundColor: '#FFFFFF',
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingVerticalXL,
    minHeight: 'calc(100vh - 128px)',
    display: 'flex',
    flexDirection: 'column',
  },
  contentHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${veracityColors.firstLight}`,
  },
  contentActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
  markdown: {
    lineHeight: 1.6,
    color: veracityColors.midnightCharcoal,
    fontSize: tokens.fontSizeBase300,
    '& h1': {
      fontSize: tokens.fontSizeHero700,
      marginTop: tokens.spacingVerticalL,
      marginBottom: tokens.spacingVerticalM,
      color: veracityColors.midnightCharcoal,
    },
    '& h2': {
      fontSize: tokens.fontSizeBase600,
      marginTop: tokens.spacingVerticalL,
      marginBottom: tokens.spacingVerticalS,
      color: veracityColors.midnightCharcoal,
    },
    '& h3': {
      fontSize: tokens.fontSizeBase500,
      marginTop: tokens.spacingVerticalM,
      marginBottom: tokens.spacingVerticalS,
      color: veracityColors.midnightCharcoal,
    },
    '& p': {
      marginTop: tokens.spacingVerticalS,
      marginBottom: tokens.spacingVerticalS,
    },
    '& ul, & ol': {
      marginTop: tokens.spacingVerticalS,
      marginBottom: tokens.spacingVerticalS,
      paddingLeft: '1.5em',
    },
    '& li': {
      marginTop: '4px',
      marginBottom: '4px',
    },
    '& code': {
      fontFamily: 'monospace',
      backgroundColor: veracityColors.firstLight,
      padding: '1px 4px',
      borderRadius: '3px',
      fontSize: '0.9em',
    },
    '& pre': {
      backgroundColor: veracityColors.firstLight,
      padding: tokens.spacingVerticalM,
      borderRadius: tokens.borderRadiusMedium,
      overflowX: 'auto',
    },
    '& pre code': {
      backgroundColor: 'transparent',
      padding: 0,
    },
    '& table': {
      borderCollapse: 'collapse',
      marginTop: tokens.spacingVerticalS,
      marginBottom: tokens.spacingVerticalS,
      fontSize: tokens.fontSizeBase200,
    },
    '& th, & td': {
      border: `1px solid ${veracityColors.firstLight}`,
      padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: veracityColors.firstLight,
      fontWeight: tokens.fontWeightSemibold,
    },
    '& a': {
      color: veracityColors.veracityOrange,
      textDecoration: 'none',
      ':hover': { textDecoration: 'underline' },
    },
    '& blockquote': {
      borderLeft: `4px solid ${veracityColors.firstLight}`,
      paddingLeft: tokens.spacingHorizontalM,
      color: veracityColors.duskBlue,
      marginLeft: 0,
    },
  },
  editor: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    flex: 1,
    minHeight: 0,
  },
  editorRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  editorBodyRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    flex: 1,
    minHeight: '320px',
  },
  editorLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: veracityColors.duskBlue,
  },
  editorTextarea: {
    width: '100%',
    flex: 1,
    minHeight: '320px',
    display: 'flex',
    '& > textarea': {
      flex: 1,
      height: '100%',
      maxHeight: 'unset',
      fontFamily: 'monospace',
      fontSize: tokens.fontSizeBase200,
      lineHeight: 1.5,
    },
  },
  modulesGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalXXS,
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
    textAlign: 'center',
    color: veracityColors.duskBlue,
  },
});

// =============================================================================
// Helpers
// =============================================================================

const KEY_PREFIX = 'guide.';

function keyForSlug(slug: string): string {
  return `${KEY_PREFIX}${slug}`;
}

function isGuideRow(row: AdminConfig): boolean {
  return row.category === 'guide' && typeof row.key === 'string' && row.key.startsWith(KEY_PREFIX);
}

function normalizeModules(raw: unknown): GuideModule[] {
  if (!Array.isArray(raw)) return ['shell'];
  const valid = raw.filter((m): m is GuideModule =>
    typeof m === 'string' && (GUIDE_MODULES as readonly string[]).includes(m),
  );
  return valid.length > 0 ? valid : ['shell'];
}

function normalizeHelpForRoutes(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const cleaned = raw
    .filter((r): r is string => typeof r === 'string')
    .map((r) => r.trim())
    .filter((r) => r.length > 0);
  return cleaned.length > 0 ? cleaned : undefined;
}

/**
 * Map saved Dataverse rows to Article entries. Rows missing a usable slug
 * are dropped silently.
 */
function loadArticles(saved: AdminConfig[]): Article[] {
  const result: Article[] = [];

  for (const row of saved) {
    if (!isGuideRow(row)) continue;
    const v = row.value as Partial<GuideArticleValue> | null | undefined;
    if (!v || typeof v !== 'object' || !v.slug) continue;
    result.push({
      slug: v.slug,
      value: {
        slug: v.slug,
        title: v.title ?? '(untitled)',
        body: v.body ?? '',
        order: typeof v.order === 'number' ? v.order : 999,
        visibility: v.visibility === 'admin' ? 'admin' : 'all',
        modules: normalizeModules((v as { modules?: unknown }).modules),
        group: typeof v.group === 'string' ? v.group : '',
        helpForRoutes: normalizeHelpForRoutes((v as { helpForRoutes?: unknown }).helpForRoutes),
      },
      configId: row.id,
      rowVersion: row.rowVersion,
    });
  }

  return result.sort((a, b) => {
    if (a.value.order !== b.value.order) return a.value.order - b.value.order;
    return a.value.title.localeCompare(b.value.title);
  });
}

function makeBlankSlug(existing: Set<string>, prefix: string): string {
  let i = 1;
  while (existing.has(`${prefix}${i}`)) i++;
  return `${prefix}${i}`;
}

// =============================================================================
// Component
// =============================================================================

export function GuidePage({ moduleFilter, adminSeesAll, allowAdminEdit }: GuidePageProps = {}) {
  const styles = useStyles();
  const { roles } = useAuth();
  const isAdmin = hasGlobalAdmin(roles);
  const canEdit = isAdmin && !!allowAdminEdit;

  const articlesQuery = useAdminConfigs('guide');
  const updateConfig = useUpdateConfig();
  const createConfig = useCreateConfig();
  const deleteConfig = useDeleteConfig();

  const articles = useMemo(
    () => loadArticles(articlesQuery.data ?? []),
    [articlesQuery.data],
  );

  // Admins on a route opted into `adminSeesAll` ignore the module filter so
  // they can browse every article in one place.
  const effectiveModuleFilter = adminSeesAll && isAdmin ? undefined : moduleFilter;

  const visibleArticles = useMemo(() => {
    return articles.filter((a) => {
      if (!isAdmin && a.value.visibility !== 'all') return false;
      if (effectiveModuleFilter && !a.value.modules.includes(effectiveModuleFilter)) return false;
      return true;
    });
  }, [articles, isAdmin, effectiveModuleFilter]);

  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<GuideArticleValue | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Drag-and-drop reorder state. The mousedown ref flag pattern gates which
  // pointer drags become real DnD operations — only drags initiated on a
  // drag handle should reorder articles.
  const [draggedSlug, setDraggedSlug] = useState<string | null>(null);
  const [dragOverSlug, setDragOverSlug] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);
  const dragHandleActive = useRef(false);

  // Default selection: first visible article on first render or after refetch.
  const effectiveSlug = useMemo(() => {
    if (selectedSlug && visibleArticles.some((a) => a.slug === selectedSlug)) return selectedSlug;
    return visibleArticles[0]?.slug ?? null;
  }, [selectedSlug, visibleArticles]);

  const current = useMemo(
    () => articles.find((a) => a.slug === effectiveSlug) ?? null,
    [articles, effectiveSlug],
  );

  const startEdit = useCallback(() => {
    if (!current) return;
    setDraft({ ...current.value, modules: [...current.value.modules] });
    setEditing(true);
    setPreviewMode(false);
    setErrorMsg(null);
  }, [current]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setDraft(null);
    setPreviewMode(false);
    setErrorMsg(null);
  }, []);

  const saveEdit = useCallback(async () => {
    if (!draft || !current) return;
    if (draft.modules.length === 0) {
      setErrorMsg('Pick at least one module for this article.');
      return;
    }
    setErrorMsg(null);
    try {
      await updateConfig.mutateAsync({
        id: current.configId,
        input: { value: draft },
        rowVersion: current.rowVersion,
      });
      setEditing(false);
      setDraft(null);
      setPreviewMode(false);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    }
  }, [draft, current, updateConfig]);

  const deleteCurrent = useCallback(async () => {
    if (!current) return;
    if (!window.confirm(`Delete "${current.value.title}"? This cannot be undone.`)) return;
    setErrorMsg(null);
    try {
      await deleteConfig.mutateAsync(current.configId);
      setEditing(false);
      setDraft(null);
      setSelectedSlug(null);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    }
  }, [current, deleteConfig]);

  const createNew = useCallback(() => {
    const slugPrefix = moduleFilter ? `${moduleFilter}-new-` : 'new-article-';
    const slug = makeBlankSlug(new Set(articles.map((a) => a.slug)), slugPrefix);
    const blank: GuideArticleValue = {
      slug,
      title: 'New article',
      body: '# New article\n\nWrite your content here using **markdown**.',
      order: (articles[articles.length - 1]?.value.order ?? 0) + 10,
      visibility: 'all',
      modules: moduleFilter ? [moduleFilter] : ['shell'],
    };
    // Persist immediately so we have a configId to edit against.
    setErrorMsg(null);
    createConfig
      .mutateAsync({
        key: keyForSlug(slug),
        category: 'guide',
        displayName: blank.title,
        description: 'In-app help article (managed via the Guide editor)',
        value: blank,
      })
      .then(() => {
        setSelectedSlug(slug);
        setDraft({ ...blank });
        setEditing(true);
        setPreviewMode(false);
      })
      .catch((err) => {
        setErrorMsg(err instanceof Error ? err.message : String(err));
      });
  }, [articles, createConfig, moduleFilter]);

  // ---------------------------------------------------------------------------
  // Drag-and-drop reorder
  // ---------------------------------------------------------------------------

  const onHandleMouseDown = useCallback(() => {
    dragHandleActive.current = true;
  }, []);

  const onHandleMouseUp = useCallback(() => {
    dragHandleActive.current = false;
  }, []);

  const onItemDragStart = useCallback((e: React.DragEvent, slug: string) => {
    if (!dragHandleActive.current) {
      e.preventDefault();
      return;
    }
    setDraggedSlug(slug);
    e.dataTransfer.setData('text/plain', slug);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const onItemDragEnd = useCallback(() => {
    setDraggedSlug(null);
    setDragOverSlug(null);
    dragHandleActive.current = false;
  }, []);

  const onItemDragOver = useCallback((e: React.DragEvent, slug: string) => {
    if (!draggedSlug) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverSlug !== slug) setDragOverSlug(slug);
  }, [draggedSlug, dragOverSlug]);

  const onItemDrop = useCallback(async (e: React.DragEvent, targetSlug: string) => {
    e.preventDefault();
    const sourceSlug = e.dataTransfer.getData('text/plain') || draggedSlug;
    setDraggedSlug(null);
    setDragOverSlug(null);
    dragHandleActive.current = false;

    if (!sourceSlug || sourceSlug === targetSlug) return;
    const source = articles.find((a) => a.slug === sourceSlug);
    const target = articles.find((a) => a.slug === targetSlug);
    if (!source || !target) return;

    // Build the new visible list with `source` removed then re-inserted just
    // before `target`. The dragged article inherits the target's group so
    // cross-group drags reassign cleanly.
    const reorderedVisible = visibleArticles.filter((a) => a.slug !== sourceSlug);
    const targetIdx = reorderedVisible.findIndex((a) => a.slug === targetSlug);
    if (targetIdx < 0) return;
    const movedSource: Article = {
      ...source,
      value: { ...source.value, group: target.value.group ?? '' },
    };
    reorderedVisible.splice(targetIdx, 0, movedSource);

    // Reassign sequential orders to the visible articles; PATCH only the rows
    // whose order or group actually changed.
    const updates: Promise<unknown>[] = [];
    for (let i = 0; i < reorderedVisible.length; i++) {
      const slot = reorderedVisible[i]!;
      const orig = articles.find((x) => x.slug === slot.slug);
      if (!orig) continue;
      const newOrder = (i + 1) * 10;
      const newGroup = slot.value.group ?? '';
      if (orig.value.order === newOrder && (orig.value.group ?? '') === newGroup) continue;
      const newValue: GuideArticleValue = {
        ...orig.value,
        order: newOrder,
        group: newGroup,
      };
      updates.push(
        updateConfig.mutateAsync({
          id: orig.configId,
          input: { value: newValue },
          rowVersion: orig.rowVersion,
        }),
      );
    }
    if (updates.length === 0) return;

    setReordering(true);
    setErrorMsg(null);
    try {
      await Promise.all(updates);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setReordering(false);
    }
  }, [draggedSlug, articles, visibleArticles, updateConfig]);

  // Group the visible articles for sidebar rendering. Group order = lowest
  // article order in that group, so admins controlling article order also
  // control group placement implicitly.
  const groupedSidebar = useMemo(() => {
    const map = new Map<string, Article[]>();
    for (const a of visibleArticles) {
      const g = a.value.group ?? '';
      const list = map.get(g) ?? [];
      list.push(a);
      map.set(g, list);
    }
    const entries = Array.from(map.entries()).map(([group, items]) => {
      items.sort((x, y) => x.value.order - y.value.order);
      const minOrder = items.reduce((m, it) => Math.min(m, it.value.order), Infinity);
      return { group, items, minOrder };
    });
    entries.sort((a, b) => a.minOrder - b.minOrder);
    return entries;
  }, [visibleArticles]);

  if (articlesQuery.isLoading) {
    return (
      <div className={styles.page}>
        <div />
        <div className={styles.empty}>
          <Spinner size="medium" label="Loading guide…" />
        </div>
      </div>
    );
  }

  if (visibleArticles.length === 0) {
    return (
      <div className={styles.page}>
        <div />
        <div className={styles.empty}>
          <BookRegular style={{ fontSize: '48px', marginBottom: tokens.spacingVerticalM }} />
          <Subtitle2>No guide articles yet</Subtitle2>
          <Body1>Ask a Vera admin to publish help content here.</Body1>
        </div>
      </div>
    );
  }

  // When the sidebar isn't constrained to a single module, show module pills
  // so the admin can tell at a glance which module each article belongs to.
  const showModulePills = isAdmin && !effectiveModuleFilter;

  return (
    <div className={styles.page}>
      {/* Sidebar */}
      <div>
        <div className={styles.sidebarHeader}>
          <BookRegular />
          <span>Guide</span>
        </div>
        <div className={styles.sidebar}>
          {groupedSidebar.map(({ group, items }) => (
            <Fragment key={group || '__ungrouped__'}>
              {group && <div className={styles.sidebarGroupHeader}>{group}</div>}
              {items.map((a) => {
                const active = a.slug === effectiveSlug;
                const isDragging = draggedSlug === a.slug;
                const isDragOver = dragOverSlug === a.slug && draggedSlug !== a.slug;
                const classes = [
                  styles.sidebarItem,
                  active ? styles.sidebarItemActive : '',
                  isDragging ? styles.sidebarItemDragging : '',
                  isDragOver ? styles.sidebarItemDragOver : '',
                ].filter(Boolean).join(' ');
                return (
                  <div
                    key={a.slug}
                    className={classes}
                    draggable={canEdit && !editing && !reordering}
                    onDragStart={(e) => onItemDragStart(e, a.slug)}
                    onDragEnd={onItemDragEnd}
                    onDragOver={(e) => onItemDragOver(e, a.slug)}
                    onDragLeave={() => setDragOverSlug((prev) => (prev === a.slug ? null : prev))}
                    onDrop={(e) => onItemDrop(e, a.slug)}
                    onClick={() => {
                      if (editing) return; // don't switch while editing
                      setSelectedSlug(a.slug);
                    }}
                    title={editing ? 'Finish or cancel the current edit before switching articles' : undefined}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                      {canEdit && (
                        <span
                          className={styles.dragHandle}
                          onMouseDown={onHandleMouseDown}
                          onMouseUp={onHandleMouseUp}
                          onClick={(e) => e.stopPropagation()}
                          title="Drag to reorder"
                        >
                          <ReOrderDotsVerticalRegular />
                        </span>
                      )}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.value.title}
                      </span>
                    </div>
                    <div className={styles.badgeRow}>
                      {showModulePills && a.value.modules.map((m) => (
                        <span key={m} className={styles.moduleBadge}>{m}</span>
                      ))}
                      {isAdmin && a.value.visibility === 'admin' && (
                        <span className={styles.visibilityBadge}>admin</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </Fragment>
          ))}
          {canEdit && (
            <div className={styles.sidebarFooter}>
              <Button
                appearance="subtle"
                icon={<AddRegular />}
                onClick={createNew}
                disabled={editing || createConfig.isPending || reordering}
                style={{ width: '100%', justifyContent: 'flex-start' }}
              >
                {reordering ? 'Reordering…' : 'New article'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <Card className={styles.content}>
        {!current ? (
          <div className={styles.empty}>
            <Body1>Select an article from the sidebar.</Body1>
          </div>
        ) : editing && draft ? (
          <EditorView
            draft={draft}
            setDraft={setDraft}
            previewMode={previewMode}
            setPreviewMode={setPreviewMode}
            isSaving={updateConfig.isPending || createConfig.isPending}
            isDeleting={deleteConfig.isPending}
            onSave={saveEdit}
            onCancel={cancelEdit}
            onDelete={deleteCurrent}
            errorMsg={errorMsg}
            styles={styles}
          />
        ) : (
          <ReadView
            article={current}
            canEdit={canEdit}
            onEdit={startEdit}
            styles={styles}
          />
        )}
      </Card>
    </div>
  );
}

// =============================================================================
// Read view
// =============================================================================

interface ReadViewProps {
  article: Article;
  canEdit: boolean;
  onEdit: () => void;
  styles: ReturnType<typeof useStyles>;
}

function ReadView({ article, canEdit, onEdit, styles }: ReadViewProps) {
  const isAdminOnly = article.value.visibility === 'admin';
  return (
    <>
      <div className={styles.contentHeader}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS }}>
          <Title2 as="h2" block>{article.value.title}</Title2>
          {isAdminOnly && (
            <Caption1 style={{ color: veracityColors.duskBlue }}>Admin-only article</Caption1>
          )}
        </div>
        {canEdit && (
          <div className={styles.contentActions}>
            <Button icon={<EditRegular />} onClick={onEdit}>
              Edit
            </Button>
          </div>
        )}
      </div>
      <div className={styles.markdown}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{article.value.body}</ReactMarkdown>
      </div>
    </>
  );
}

// =============================================================================
// Editor view
// =============================================================================

interface EditorViewProps {
  draft: GuideArticleValue;
  setDraft: (next: GuideArticleValue) => void;
  previewMode: boolean;
  setPreviewMode: (next: boolean) => void;
  isSaving: boolean;
  isDeleting: boolean;
  onSave: () => void;
  onCancel: () => void;
  onDelete: () => void;
  errorMsg: string | null;
  styles: ReturnType<typeof useStyles>;
}

function EditorView({
  draft,
  setDraft,
  previewMode,
  setPreviewMode,
  isSaving,
  isDeleting,
  onSave,
  onCancel,
  onDelete,
  errorMsg,
  styles,
}: EditorViewProps) {
  const update = (patch: Partial<GuideArticleValue>) => setDraft({ ...draft, ...patch });

  const toggleModule = (m: GuideModule, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...draft.modules, m]))
      : draft.modules.filter((x) => x !== m);
    update({ modules: next });
  };

  return (
    <div className={styles.editor}>
      <div className={styles.contentHeader}>
        <Title2>Editing: {draft.title}</Title2>
        <div className={styles.contentActions}>
          <Button
            icon={<EyeRegular />}
            onClick={() => setPreviewMode(!previewMode)}
            appearance={previewMode ? 'primary' : 'secondary'}
          >
            {previewMode ? 'Editing' : 'Preview'}
          </Button>
          <Button appearance="primary" icon={<SaveRegular />} onClick={onSave} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
          <Button icon={<DismissRegular />} onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            icon={<DeleteRegular />}
            onClick={onDelete}
            disabled={isSaving || isDeleting}
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </div>

      {errorMsg && (
        <MessageBar intent="error">
          <MessageBarBody>{errorMsg}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.editorRow}>
        <span className={styles.editorLabel}>Title</span>
        <Input value={draft.title} onChange={(_, data) => update({ title: data.value })} />
      </div>

      <div style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
        <div className={styles.editorRow} style={{ flex: 1 }}>
          <span className={styles.editorLabel}>Visibility</span>
          <Dropdown
            value={draft.visibility === 'admin' ? 'Admins only' : 'All Vera users'}
            selectedOptions={[draft.visibility]}
            onOptionSelect={(_, data) => {
              const v = data.optionValue as 'all' | 'admin' | undefined;
              if (v) update({ visibility: v });
            }}
          >
            <Option value="all" text="All Vera users">All Vera users</Option>
            <Option value="admin" text="Admins only">Admins only</Option>
          </Dropdown>
        </div>
        <div className={styles.editorRow} style={{ flex: 1 }}>
          <span className={styles.editorLabel}>Group</span>
          <Input
            placeholder="Optional — articles with the same group cluster in the sidebar"
            value={draft.group ?? ''}
            onChange={(_, data) => update({ group: data.value })}
          />
        </div>
        <div className={styles.editorRow} style={{ width: '120px' }}>
          <span className={styles.editorLabel}>Sidebar order</span>
          <Input
            type="number"
            value={String(draft.order)}
            onChange={(_, data) => update({ order: Number(data.value) || 0 })}
          />
        </div>
        <div className={styles.editorRow} style={{ flex: 1 }}>
          <span className={styles.editorLabel}>Slug (URL key)</span>
          <Input value={draft.slug} disabled title="Slug cannot be changed after creation" />
        </div>
      </div>

      <div className={styles.editorRow}>
        <span className={styles.editorLabel}>Show in modules</span>
        <div className={styles.modulesGrid}>
          {GUIDE_MODULES.map((m) => (
            <Checkbox
              key={m}
              checked={draft.modules.includes(m)}
              onChange={(_, data) => toggleModule(m, data.checked === true)}
              label={m}
            />
          ))}
        </div>
      </div>

      <div className={styles.editorRow}>
        <span className={styles.editorLabel}>Help on routes</span>
        <Input
          placeholder="Optional — comma-separated route paths, e.g. /sales/one-card, /sales/dashboard"
          value={(draft.helpForRoutes ?? []).join(', ')}
          onChange={(_, data) => {
            const parts = data.value
              .split(',')
              .map((p) => p.trim())
              .filter((p) => p.length > 0);
            update({ helpForRoutes: parts.length > 0 ? parts : undefined });
          }}
        />
      </div>

      <div className={styles.editorBodyRow}>
        <span className={styles.editorLabel}>Body (markdown)</span>
        {previewMode ? (
          <Card style={{ padding: tokens.spacingVerticalM, flex: 1, minHeight: 0, overflow: 'auto' }}>
            <div className={styles.markdown}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{draft.body}</ReactMarkdown>
            </div>
          </Card>
        ) : (
          <Textarea
            className={styles.editorTextarea}
            textarea={{
              style: {
                height: '100%',
                maxHeight: 'unset',
                flex: 1,
                fontFamily: 'monospace',
                fontSize: '13px',
                lineHeight: 1.5,
              },
            }}
            value={draft.body}
            onChange={(_, data) => update({ body: data.value })}
            resize="none"
          />
        )}
      </div>
    </div>
  );
}

export default GuidePage;
